import React, { useMemo } from 'react'
import { Box, Dialog, Typography, SxProps, Theme, TextField } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { type GridColDef } from '@mui/x-data-grid-pro'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { useModal } from '../../../../../store/ModalProvider'
import { popupActionButtonSx } from '../popupButtonStyles'
import { popupInputLabelSx } from '../popupInputStyles'
import type { ServiceItineraryModalData } from '../../../../../types/modal'

type InfoFieldProps = {
	label: string
	value?: string
	endAdornment?: React.ReactNode
	sx?: SxProps<Theme>
	multiline?: boolean
}

type ItineraryStopRow = {
	id: string
	date: string
	startTime: string
	endTime: string
	description: string
}

const defaultItineraryData: ServiceItineraryModalData = {
	serviceId: '0000',
	serviceType: 'Agência de Viagens',
	requestCode: '1115_PE2_PNE2',
	startDateTime: '02/05/2024 09:00',
	endDateTime: '02/05/2024 10:00',
	observations:
		"It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English.",
	client: {
		number: '611',
		name: 'Best Transfers 4 U',
		address: 'Ctro.Escrit.Panoramic-Av.Atlantico 16 esq 07.09',
		postalCode: '1990-019',
		city: 'Lisboa',
		phone: '217707280',
		observations: 'Tristique massa et dui nulla interdum. Et ac nibh placerat justo est enim',
	},
	itinerary: {
		description: 'Ericeira - Mafra',
		purpose: 'Circuito de Turismo',
		observations:
			"It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English.",
		stops: [
			{
				date: 'seg 02/05/2024',
				startTime: '09:00',
				endTime: '09:29',
				description: 'Ericeira Centro',
			},
			{
				date: 'seg 02/05/2024',
				startTime: '09:30',
				endTime: '09:44',
				description: 'Sobreiro',
			},
			{
				date: 'seg 02/05/2024',
				startTime: '09:45',
				endTime: '10:00',
				description: 'Mafra Centro',
			},
		],
	},
	expenses: {
		driver: 'Despesas do(s) Motorista(s) são por conta de Transol - Transportes e Turismo SA',
		tolls: 'Portagens + IVA são por conta de Transol - Transportes e Turismo SA',
	},
}

const sectionTitleSx = {
	fontFamily: 'Poppins, sans-serif',
	fontWeight: 600,
	fontSize: '20px',
	lineHeight: 1.3,
	color: '#092056',
}

const dialogTitleSx = {
	fontFamily: 'Poppins, sans-serif',
	fontWeight: 600,
	fontSize: '16px',
	lineHeight: 1.75,
	color: '#092056',
	letterSpacing: '0.15px',
}

const mainTitleSx = {
	fontFamily: 'Poppins, sans-serif',
	fontWeight: 600,
	fontSize: '24px',
	lineHeight: 1.334,
	color: '#092056',
}

const readonlyFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#F5F7FC',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
		WebkitTextFillColor: '#092056',
	},
	'& .MuiOutlinedInput-inputMultiline': {
		padding: '8px 12px',
	},
}

const tableHeaderCellSx = {
	fontFamily: 'Poppins, sans-serif',
	fontWeight: 600,
	fontSize: '12px',
	lineHeight: '24px',
	color: '#8695AF',
	letterSpacing: '0.17px',
}

const tableCellSx = {
	fontFamily: 'Poppins, sans-serif',
	fontWeight: 400,
	fontSize: '12px',
	lineHeight: 1.3,
	color: '#092056',
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		...tableHeaderCellSx,
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		...tableCellSx,
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const InfoField = ({ label, value, endAdornment, sx, multiline }: InfoFieldProps) => (
	<TextField
		label={label}
		value={value || '--'}
		size='small'
		multiline={multiline}
		minRows={multiline ? 2 : undefined}
		InputProps={{ readOnly: true, endAdornment }}
		InputLabelProps={{ shrink: Boolean(label) }}
		sx={{ ...readonlyFieldSx, width: '100%', ...(sx ?? {}) }}
	/>
)

const CustomModalServiceItinerary: React.FC = () => {
	const { isOpen, closeModal, modalData } = useModal()
	const itineraryData = modalData as ServiceItineraryModalData | undefined

	const data = useMemo(() => {
		const incoming = itineraryData ?? defaultItineraryData
		return {
			...defaultItineraryData,
			...incoming,
			client: {
				...defaultItineraryData.client,
				...incoming.client,
			},
			itinerary: {
				...defaultItineraryData.itinerary,
				...incoming.itinerary,
				stops: incoming.itinerary?.stops?.length
					? incoming.itinerary.stops
					: defaultItineraryData.itinerary?.stops ?? [],
			},
			expenses: {
				...defaultItineraryData.expenses,
				...incoming.expenses,
			},
		}
	}, [itineraryData])

	const stops = data.itinerary?.stops ?? []
	const stopRows = useMemo(
		() =>
			stops.map((stop, index) => ({
				id: `${stop.date}-${stop.startTime}-${index}`,
				date: stop.date,
				startTime: stop.startTime,
				endTime: stop.endTime,
				description: stop.description,
			})),
		[stops],
	)
	const stopColumns = useMemo<GridColDef<ItineraryStopRow>[]>(
		() => [
			{
				field: 'date',
				headerName: 'Data',
				width: 130,
			},
			{
				field: 'startTime',
				headerName: 'Hora Início',
				width: 130,
			},
			{
				field: 'endTime',
				headerName: 'Hora Fim',
				width: 130,
			},
			{
				field: 'description',
				headerName: 'Descrição',
				flex: 1,
				minWidth: 220,
			},
		],
		[],
	)

	if (!itineraryData) {
		return null
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 100%)',
					borderRadius: '4px',
					backgroundColor: '#FFFFFF',
					boxShadow:
						'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box sx={{ padding: '16px 24px 8px' }}>
					<Typography sx={dialogTitleSx}>Requisição do Serviço</Typography>
				</Box>

				<Box
					sx={{
						padding: '8px 24px 16px',
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<Typography sx={mainTitleSx}>Itinerário do Serviço</Typography>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						<Typography sx={sectionTitleSx}>Requisição</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
							<InfoField
								label='Tipo de Serviço'
								value={data.serviceType}
								endAdornment={<KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#00A5FF' }} />}
								sx={{ flex: '1 1 220px' }}
							/>
							<InfoField
								label='Chapa/Requisição'
								value={data.requestCode}
								sx={{ flex: '1 1 220px' }}
							/>
							<InfoField
								label='Início'
								value={data.startDateTime}
								sx={{ flex: '1 1 180px' }}
							/>
							<InfoField
								label='Término'
								value={data.endDateTime}
								sx={{ flex: '1 1 180px' }}
							/>
						</Box>
						<InfoField
							label='Observações'
							value={data.observations}
							multiline
						/>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						<Typography sx={sectionTitleSx}>Cliente</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
							<InfoField
								label='Nr Cliente'
								value={data.client?.number}
								sx={{ width: '140px' }}
							/>
							<InfoField
								label='Nome'
								value={data.client?.name}
								sx={{ flex: '1 1 320px' }}
							/>
						</Box>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
							<InfoField
								label='Morada'
								value={data.client?.address}
								sx={{ flex: '1 1 420px' }}
							/>
							<InfoField
								label='Código Postal'
								value={data.client?.postalCode}
								sx={{ width: '150px' }}
							/>
							<InfoField
								label='Local'
								value={data.client?.city}
								sx={{ width: '140px' }}
							/>
							<InfoField
								label='Telefone'
								value={data.client?.phone}
								sx={{ width: '140px' }}
							/>
						</Box>
						<InfoField
							label='Observações do Comercial'
							value={data.client?.observations}
							multiline
						/>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						<Typography sx={sectionTitleSx}>Itinerário</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
							<InfoField
								label='Descrição'
								value={data.itinerary?.description}
								sx={{ flex: '1 1 420px' }}
							/>
							<InfoField
								label='Finalidade'
								value={data.itinerary?.purpose}
								sx={{ flex: '1 1 220px' }}
							/>
						</Box>
						<InfoField
							label='Observações do Itinerário'
							value={data.itinerary?.observations}
							multiline
						/>
						<Box sx={{ width: '100%' }}>
							<AnimatedDataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={stopRows}
								columns={stopColumns}
								density='compact'
								autoHeight
								disableRowSelectionOnClick
								hideFooter
								rowHeight={32}
								columnHeaderHeight={36}
								sx={tableSx}
							/>
						</Box>
					</Box>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						<Typography sx={sectionTitleSx}>Despesas</Typography>
						<InfoField
							label='Despesas do Motorista'
							value={data.expenses?.driver}
							multiline
						/>
						<InfoField
							label='Despesas s/ portagens'
							value={data.expenses?.tolls}
							multiline
						/>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						padding: '6px 16px 12px',
						backgroundColor: '#FFFFFF',
					}}
				>
					<CustomButton
						variant='contained'
						onClick={closeModal}
						sx={popupActionButtonSx}
					>
						Fechar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalServiceItinerary
